/**
 * Multi-frame moderation sampling for conversation video.
 * Short clips use one JPEG grid; long clips use several segment grids (multiple conv_scan parts).
 */

import {
  captureVideoFrameJpegsAtUniqueSeekTimes,
  getVideoDimensions,
  mergeSeekTimesForVideoCapture,
  type VideoLoadOptions,
} from './videoProcessing';

export type ModerationFrameTimeOptions = {
  minFrames?: number;
  maxFrames?: number;
  /** Densest spacing to sample at. Widens automatically when duration/maxFrames exceeds it. */
  intervalSec?: number;
  /**
   * Lower bound for any seek; avoids seeking to exactly 0, which some decoders handle poorly.
   * Sampling starts here, so a late bound narrows the span rather than piling samples onto it.
   */
  earlySeekSec?: number;
  /**
   * Half-width of the random offset applied per sample, clamped to half a stride so a
   * sample never leaves its own stratum. Defaults to a full stride (uniform within the
   * stratum). Use `0` for deterministic midpoints (tests, reproducibility).
   */
  jitterSec?: number;
  /** Uniform [0,1); inject in tests for deterministic jitter. */
  random?: () => number;
};

/** Defaults for {@link buildModerationFrameTimes} (exported for tests and tuning). */
export const DEFAULT_MODERATION_FRAME_TIME_OPTIONS = {
  minFrames: 3,
  maxFrames: 12,
  intervalSec: 7,
  earlySeekSec: 0.1,
} as const;

/**
 * Above this duration (seconds), moderation uses multiple JPEG grids (one conv_scan part each)
 * so long clips get coverage across the timeline. Must stay within Lambda batch limits (~32 images).
 */
export const LONG_VIDEO_MODERATION_SPLIT_AFTER_SEC = 90;

/** Target segment length when splitting (seconds). */
export const MODERATION_SCAN_PART_TARGET_DURATION_SEC = 45;

/**
 * Hard ceiling on scan parts, so total moderation cost is bounded for any duration: a
 * two-hour video produces the same 8 grids as a six-minute one. Beyond this, segments widen
 * and {@link buildModerationFrameTimes} spreads its samples to match rather than clustering.
 * Also keeps one video within the per-request upload rate limit, which each part consumes.
 */
export const MODERATION_SCAN_MAX_PARTS = 8;

/**
 * How many scan JPEG parts to generate for a video of this length.
 * Short clips use one composite; longer clips use several segment grids.
 */
export function moderationVideoScanPartCountForDuration(durationSeconds: number): number {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return 1;
  }
  if (durationSeconds <= LONG_VIDEO_MODERATION_SPLIT_AFTER_SEC) {
    return 1;
  }
  return Math.min(
    MODERATION_SCAN_MAX_PARTS,
    Math.ceil(durationSeconds / MODERATION_SCAN_PART_TARGET_DURATION_SEC)
  );
}

function clampSeek(t: number, durationSeconds: number): number {
  return Math.min(Math.max(0, t), Math.max(0, durationSeconds - 0.05));
}

/**
 * Stratified seek timestamps for moderation stills.
 *
 * The seekable span — `earlySeekSec` to the end — is divided into equal strata and one sample is
 * drawn from each, so coverage stays uniform at any duration. A fixed interval would instead
 * cluster every sample into the first `maxFrames * intervalSec` seconds and leave the remainder
 * of a long span unsampled.
 */
export function buildModerationFrameTimes(
  durationSeconds: number,
  options?: ModerationFrameTimeOptions
): number[] {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    throw new Error('Invalid video duration');
  }

  // Callers build option objects by spreading, which can set keys to an explicit `undefined`
  // that would survive `{...defaults, ...options}` and turn the arithmetic below into NaN.
  const D = DEFAULT_MODERATION_FRAME_TIME_OPTIONS;
  const num = (v: number | undefined, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) ? v : fallback;

  const maxFrames = Math.max(1, Math.floor(num(options?.maxFrames, D.maxFrames)));
  const minFrames = Math.min(
    Math.max(1, Math.floor(num(options?.minFrames, D.minFrames))),
    maxFrames
  );
  const earlySeekSec = Math.max(0, num(options?.earlySeekSec, D.earlySeekSec));
  const intervalSec = Math.max(num(options?.intervalSec, D.intervalSec), 1e-6);

  // Ceil, not floor: the smallest count whose stride stays within `intervalSec`. Flooring would
  // sample short clips one frame more sparsely than a fixed-interval walk over the same span.
  const byInterval = Math.ceil(durationSeconds / intervalSec);
  const count = Math.min(maxFrames, Math.max(minFrames, byInterval));

  // Strata cover the seekable span only. Laying them over the whole duration and clamping
  // afterwards would push every stratum below `earlySeekSec` onto the bound itself, collapsing
  // a late bound's samples into a single seek instead of spreading them over what remains.
  const spanStartSec = Math.min(earlySeekSec, Math.max(0, durationSeconds - 0.05));
  const strideSec = (durationSeconds - spanStartSec) / count;

  // Clamped to half a stride so a jittered sample stays inside its own stratum.
  const jitterSec = Math.min(num(options?.jitterSec, strideSec), strideSec / 2);
  const rng = jitterSec > 0 ? (options?.random ?? Math.random) : () => 0.5;

  const times: number[] = [];
  for (let i = 0; i < count; i++) {
    const midpoint = spanStartSec + (i + 0.5) * strideSec;
    const offset = jitterSec > 0 ? (rng() * 2 - 1) * jitterSec : 0;
    times.push(clampSeek(Math.max(earlySeekSec, midpoint + offset), durationSeconds));
  }

  // Strata are distinct by construction; this only collapses samples on very short spans
  // where a whole stride is under the 250ms frame-capture merge window.
  const merged: number[] = [];
  for (const t of times) {
    if (merged.length === 0 || t - merged[merged.length - 1]! >= 0.25) {
      merged.push(t);
    }
  }
  return merged;
}

/**
 * Lay out frame JPEGs in a grid → one JPEG for a single scan upload.
 */
export async function composeModerationScanFrameGrid(
  frames: Blob[],
  options?: { maxCellDim?: number; jpegQuality?: number }
): Promise<Blob> {
  const maxCell = options?.maxCellDim ?? 256;
  const quality = options?.jpegQuality ?? 0.82;
  if (frames.length === 0) {
    throw new Error('No moderation frames');
  }
  if (frames.length === 1) {
    return frames[0]!;
  }

  const bitmaps = await Promise.all(frames.map((b) => createImageBitmap(b)));
  try {
    const n = bitmaps.length;
    const cols = Math.ceil(Math.sqrt(n));
    const rows = Math.ceil(n / cols);
    let cellW = 0;
    let cellH = 0;
    for (const bmp of bitmaps) {
      const scale = Math.min(1, maxCell / Math.max(bmp.width, bmp.height));
      cellW = Math.max(cellW, Math.round(bmp.width * scale));
      cellH = Math.max(cellH, Math.round(bmp.height * scale));
    }
    const canvas = new OffscreenCanvas(cols * cellW, rows * cellH);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to create 2D canvas context');

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    for (let i = 0; i < bitmaps.length; i++) {
      const bmp = bitmaps[i]!;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const scale = Math.min(1, maxCell / Math.max(bmp.width, bmp.height));
      const w = Math.round(bmp.width * scale);
      const h = Math.round(bmp.height * scale);
      const x = col * cellW + (cellW - w) / 2;
      const y = row * cellH + (cellH - h) / 2;
      ctx.drawImage(bmp, x, y, w, h);
    }
    return await canvas.convertToBlob({ type: 'image/jpeg', quality });
  } finally {
    for (const b of bitmaps) {
      b.close();
    }
  }
}

export type BuildVideoModerationScanCompositeOptions = VideoLoadOptions &
  ModerationFrameTimeOptions & {
    gridMaxCellDim?: number;
    gridJpegQuality?: number;
    thumbMaxDim?: number;
    thumbQuality?: number;
  };

export type BuildVideoModerationScanPayloadsOptions = BuildVideoModerationScanCompositeOptions & {
  /** When set (e.g. in tests), skips duration-based part count. */
  partCountOverride?: number;
  /**
   * When set, skips a redundant video load to read duration (e.g. already obtained from
   * {@link getVideoDimensionsAndScanThumbnail} in the E2E upload path).
   */
  precomputedVideoDurationSeconds?: number;
};

export type VideoModerationScanPayload = {
  body: Blob;
  contentType: 'image/jpeg';
};

/**
 * Builds one or more JPEG grids (timeline segments) for conv_scan image moderation.
 * Long videos use multiple parts; see {@link moderationVideoScanPartCountForDuration}.
 */
export async function buildVideoModerationScanPayloads(
  file: File,
  options?: BuildVideoModerationScanPayloadsOptions
): Promise<VideoModerationScanPayload[]> {
  const {
    partCountOverride,
    precomputedVideoDurationSeconds,
    gridMaxCellDim,
    gridJpegQuality,
    thumbMaxDim,
    thumbQuality,
    minFrames,
    maxFrames,
    intervalSec,
    earlySeekSec,
    jitterSec,
    random,
    ...loadOpts
  } = options ?? {};

  const durationSeconds =
    precomputedVideoDurationSeconds !== undefined
      ? precomputedVideoDurationSeconds
      : (await getVideoDimensions(file, loadOpts)).durationSeconds;
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    throw new Error('Invalid video duration');
  }
  const partCount =
    partCountOverride ?? moderationVideoScanPartCountForDuration(durationSeconds);

  const captureOpts = {
    ...loadOpts,
    maxThumbnailDim: thumbMaxDim,
    thumbnailQuality: thumbQuality,
  };

  const perPartMerged: number[][] = [];
  for (let i = 0; i < partCount; i++) {
    const t0 = (durationSeconds * i) / partCount;
    const t1 = (durationSeconds * (i + 1)) / partCount;
    const segDur = Math.max(t1 - t0, 0.05);

    // Per-segment budget. `intervalSec` is the densest spacing, not the actual one:
    // buildModerationFrameTimes widens the stride to segDur/maxFrames on long segments so
    // samples cover the whole segment instead of bunching at its head.
    const timeOpts =
      partCount === 1
        ? { minFrames, maxFrames, intervalSec, earlySeekSec, jitterSec, random }
        : {
            minFrames: 2,
            maxFrames: 6,
            intervalSec: 7,
            earlySeekSec: 0.05,
            jitterSec,
            random,
          };

    const localTimes = buildModerationFrameTimes(segDur, timeOpts);
    const absoluteTimes = localTimes.map((t) =>
      Math.min(Math.max(0, t0 + t), durationSeconds - 0.05)
    );
    perPartMerged.push(mergeSeekTimesForVideoCapture(absoluteTimes, durationSeconds));
  }

  const allUniqueSeeks = [...new Set(perPartMerged.flat())].sort((a, b) => a - b);
  const frameMap = await captureVideoFrameJpegsAtUniqueSeekTimes(
    file,
    allUniqueSeeks,
    captureOpts
  );

  const payloads: VideoModerationScanPayload[] = [];
  for (let i = 0; i < partCount; i++) {
    const merged = perPartMerged[i]!;
    const thumbs = merged.map((t) => {
      const b = frameMap.get(t);
      if (!b) {
        throw new Error('Missing video frame for moderation sample');
      }
      return b;
    });
    const body = await composeModerationScanFrameGrid(thumbs, {
      maxCellDim: gridMaxCellDim,
      jpegQuality: gridJpegQuality,
    });
    payloads.push({ body, contentType: 'image/jpeg' });
  }

  return payloads;
}

/**
 * Builds one JPEG (grid of sampled frames) for conv_scan image moderation.
 * Prefer {@link buildVideoModerationScanPayloads} when uploading (multi-part for long video).
 */
export async function buildVideoModerationScanComposite(
  file: File,
  options?: BuildVideoModerationScanCompositeOptions
): Promise<Blob> {
  const payloads = await buildVideoModerationScanPayloads(file, {
    ...options,
    partCountOverride: 1,
  });
  return payloads[0]!.body;
}
